import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { AdLabel } from 'client/site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';
import { addZipCode, definePlatform } from 'client/site-modules/shared/components/native-ad/utils/utils';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import {
  SRPCreativeConfigDefaultProps,
  SRPCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import {
  AdUnitDefaultProps,
  AdUnitPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'client/site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { SRP_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import {
  getDisplayHeadline,
  getTrackingValue,
} from 'site-modules/shared/components/native-ad/utils/offer-incentives-ad-utils';

import './srp-site-served-ad.scss';

const CTA = 'VIEW ALL OFFERS';
const HEADLINE_DEFAULT = 'Special offers available';

export function SrpSiteServedAdUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  creativeConfig = SRPCreativeConfigDefaultProps,
  mobile = SiteServedAdDefaultProps.mobile,
  currentVehicle = SiteServedAdDefaultProps.currentVehicle,
  dependedOnCurrentVehicle = false,
  customTargeting = AdUnitDefaultProps.customTargeting,
  position = SiteServedAdDefaultProps.position,
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    ipZipCode,
    offersNumber,
    creativeConfigData: {
      headline,
      logo,
      photo,
      photoDisclaimer,
      lineItemId,
      siteServedCreativeId,
      linkDisplayUrl,
      cta,
      target,
    },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      const trackingValue = getTrackingValue(headline, offersNumber, zipCode, ipZipCode, HEADLINE_DEFAULT, 14, 10);
      adLoadTrackingListener(event, trackingValue);
    },
    [adLoadTrackingListener, headline, ipZipCode, offersNumber, zipCode]
  );

  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';

  const headlineText = getDisplayHeadline(headline, offersNumber, zipCode, ipZipCode, HEADLINE_DEFAULT, 14, 10);

  let description = '';
  if (!headline && offersNumber) {
    description = 'Edmunds data shows offers may be available near you';
  }
  const oemLogoInlineStyles = mobile
    ? { maxHeight: '30px', position: 'absolute', top: '0.25rem', left: '0.5rem' }
    : { maxHeight: '30px' };
  const oemLogo = (
    <div className={classnames({ 'oem-logo-top-left': mobile, 'mb-0_5': !mobile })} style={oemLogoInlineStyles}>
      <img className="oem-logo" src={logo} alt="" style={{ maxHeight: '30px' }} />
    </div>
  );

  return (
    <div className="srp-ssa rounded h-100 w-100" ref={adRef}>
      <DfpTracking
        lineItemId={lineItemId}
        siteServedCreativeId={siteServedCreativeId}
        mobile={mobile}
        nativeStyle={SRP_AD.NATIVE_STYLE}
        currentVehicle={currentVehicle}
        dependedOnCurrentVehicle={dependedOnCurrentVehicle}
        customTargeting={customTargeting}
        position={position}
        slotRenderEndListener={loadTrackingListener}
        siteServedAdsUpdater={siteServedAdsUpdater}
      />
      {adTrackers}
      <a
        href={ctaURL}
        target={ctaTarget}
        className="site-served-ad-link no-focus text-decoration-none card pos-r d-flex flex-column flex-md-row"
        rel="noopener noreferrer"
        onClick={adClickTrackingListener}
      >
        <AdLabel />
        {mobile && oemLogo}
        <div className={classnames('img-container pos-r text-center', { 'align-self-center col-md-4': !mobile })}>
          <AspectRatioContainer aspectRatio="16:9" className={'jelly-bean mx-auto'}>
            <img src={photo} alt="" />
          </AspectRatioContainer>
          <div className="hidden-sm-up disclaimer persistent-disclaimer text-gray text-end text-md-start align-self-md-end">
            {photoDisclaimer}
          </div>
        </div>
        <div className="info-container text-center pos-r d-flex flex-column col-md-8 p-0 px-0_75 px-md-1">
          <div className="d-flex flex-column flex-md-row justify-content-between h-100">
            <div className="info-title text-gray-darker text-md-start size-16 fw-bold mt-0_5 mb-1">
              <div>
                {!mobile && oemLogo}
                <div
                  className={classnames('description text-gray-dark mt-md-1_5 mb-0_75', {
                    'hidden-md-down': !description,
                    small: !mobile,
                    xsmall: mobile,
                  })}
                >
                  {description}
                </div>
              </div>
            </div>
            <div className="info-offers text-md-center d-flex flex-column justify-content-center mb-0_5 mb-md-1 mt-md-0_5">
              <div>
                <ContentFragment classes="headline size-16 text-primary fw-bold">{headlineText}</ContentFragment>
                <div className="cta btn mt-0_5 px-0_5 btn-blue-50 mx-0 py-0_5 w-100">
                  <CtaButtonText text={cta || CTA} />
                </div>
              </div>
            </div>
          </div>
          <div className="hidden-md-down disclaimer persistent-disclaimer text-gray text-end text-md-start align-self-md-end">
            {photoDisclaimer}
          </div>
          <div className="pb-0_75 py-md-0_75 d-flex text-gray-darker align-items-md-center px-0 w-100">
            <div className="small text-md-start w-50 hidden-md-down">Visit {linkDisplayUrl} for details</div>
            <div
              className={classnames('disclaimer-label small text-center', {
                'text-blue-50 w-100': mobile,
                'w-50': !mobile,
              })}
              role="presentation"
            >
              {linkDisplayUrl}
            </div>
          </div>
        </div>
      </a>
    </div>
  );
}

SrpSiteServedAdUI.propTypes = {
  ...SiteServedAdPropTypes,
  creativeConfig: SRPCreativeConfigPropTypes,
  dependedOnCurrentVehicle: PropTypes.bool,
  customTargeting: AdUnitPropTypes.customTargeting,
};

export const SrpSiteServedAd = siteServedAdTrackingWrapper(SrpSiteServedAdUI);
